/**
 * FileUploadPage.js
 * 
 * Drag-and-drop file upload interface for sharing files with the development team.
 * Files are uploaded to the server and can be downloaded by anyone with access.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const FileUploadPage = () => {
  const { instance, accounts } = useMsal();
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const categories = [
    { id: 'general', label: 'General', icon: '📁' },
    { id: 'coding-structure', label: 'Coding Structure', icon: '📊' },
    { id: 'templates', label: 'Templates', icon: '📋' },
    { id: 'documentation', label: 'Documentation', icon: '📄' },
    { id: 'images', label: 'Images', icon: '🖼️' },
    { id: 'reports', label: 'Reports', icon: '📈' },
    { id: 'bid-log', label: 'Bid Log', icon: '📋', help: 'Excel with Quote #, Company, Client Name, etc. — imports into Bid Log table' },
    { id: 'schedule-pdf', label: 'Schedule PDF → Excel', icon: '📅', help: 'Upload a P6 schedule PDF — extracts Activity ID, Activity Name, Duration, Start, Finish and downloads as Excel' }
  ];

  // Fetch uploaded files
  const fetchUploadedFiles = useCallback(async () => {
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', '/upload/files');
      if (result.data?.success) {
        setUploadedFiles(result.data.files || []);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchUploadedFiles();
  }, [fetchUploadedFiles]);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  // Remove file from queue
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle schedule PDF → Excel conversion (special flow)
  const handleSchedulePdfUpload = async (file, index) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/upload/parse-schedule-pdf',
        formData,
        { isFormData: true, responseType: 'blob' }
      );

      // Response is a blob (Excel file) — trigger download
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const originalName = file.name.replace(/\.pdf$/i, '');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${originalName}_schedule.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setUploadProgress(prev => ({ ...prev, [index]: 100 }));
      return { file: file.name, success: true, message: 'Schedule parsed — Excel downloaded!' };
    } catch (err) {
      let errMsg = 'Failed to parse schedule PDF';
      if (err.response?.data) {
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            errMsg = json.error || errMsg;
          } catch (_) {}
        } else {
          errMsg = err.response.data.error || errMsg;
        }
      }
      return { file: file.name, success: false, error: errMsg };
    }
  };

  // Upload files
  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(prev => ({ ...prev, [i]: 0 }));

      // Schedule PDF category uses a special endpoint that returns an Excel download
      if (selectedCategory === 'schedule-pdf') {
        const result = await handleSchedulePdfUpload(file, i);
        results.push(result);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('category', selectedCategory);
        formData.append('file', file);

        const response = await authenticatedApiCall(
          instance, 
          accounts, 
          'POST', 
          '/upload', 
          formData,
          { isFormData: true }
        );

        if (response.data?.success) {
          results.push({ file: file.name, success: true, message: response.data?.message });
          setUploadProgress(prev => ({ ...prev, [i]: 100 }));
        } else {
          results.push({ file: file.name, success: false, error: response.data?.error });
        }
      } catch (err) {
        const errMsg = err.response?.data?.error || err.message;
        results.push({ file: file.name, success: false, error: errMsg });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failed = results.filter(r => !r.success);
    const successMsg = results.find(r => r.success)?.message;
    if (failed.length > 0) {
      const firstError = failed[0].error || 'Unknown error';
      showToast(`Uploaded ${successCount}/${files.length}. Failed: ${firstError}`, 'error');
    } else if (successMsg) {
      showToast(successMsg);
    } else {
      showToast(`Uploaded ${successCount} of ${files.length} files`);
    }
    
    setFiles([]);
    setUploadProgress({});
    setUploading(false);
    fetchUploadedFiles();
  };

  // Delete file
  const handleDelete = async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;

    try {
      const result = await authenticatedApiCall(instance, accounts, 'DELETE', `/upload/${fileId}`);
      if (result.data?.success) {
        showToast('File deleted');
        fetchUploadedFiles();
      }
    } catch (err) {
      showToast('Failed to delete file', 'error');
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get file icon
  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const icons = {
      pdf: '📕',
      xlsx: '📗', xls: '📗', xlsm: '📗',
      doc: '📘', docx: '📘',
      png: '🖼️', jpg: '🖼️', jpeg: '🖼️', gif: '🖼️',
      zip: '📦', rar: '📦',
      txt: '📝', csv: '📝'
    };
    return icons[ext] || '📄';
  };

  return (
    <div style={{ padding: '1.5rem', backgroundColor: '#f8f9fa', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.75rem', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          📤 File Upload
        </h1>
        <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>
          Upload and share files with the team. Drag and drop or click to select files.
        </p>
      </div>

      {/* Upload Area */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        padding: '1.5rem', 
        marginBottom: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {/* Category Selection */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
            Category:
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                title={cat.help}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: selectedCategory === cat.id ? '2px solid #dc3545' : '1px solid #d1d5db',
                  backgroundColor: selectedCategory === cat.id ? '#fef2f2' : 'white',
                  color: selectedCategory === cat.id ? '#dc3545' : '#374151',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: selectedCategory === cat.id ? '600' : '400'
                }}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          {selectedCategory === 'bid-log' && (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
              Upload an Excel file with columns: Quote #, Company, Client Name, Description, Contract Type, Lead, Crew, Budget, Won/Lost, Status, Comments. Data will be imported into the Bid Log table.
            </p>
          )}
          {selectedCategory === 'schedule-pdf' && (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#0369a1' }}>
              Upload a P6 schedule PDF. The system will extract Activity ID, Activity Name, Duration, Start, and Finish columns and download them as an Excel file. The PDF must contain a table with selectable text (not a scanned image).
            </p>
          )}
        </div>

        {/* Drag and Drop Zone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragActive ? '#dc3545' : '#d1d5db'}`,
            borderRadius: '12px',
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: dragActive ? '#fef2f2' : '#f9fafb',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {dragActive ? '📥' : '📁'}
          </div>
          <p style={{ margin: 0, color: '#374151', fontSize: '1.1rem', fontWeight: '500' }}>
            {dragActive ? 'Drop files here!' : 'Drag and drop files here'}
          </p>
          <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
            or click to browse
          </p>
        </div>

        {/* Selected Files Queue */}
        {files.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#374151' }}>
              Files to Upload ({files.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {files.map((file, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>{getFileIcon(file.name)}</span>
                    <div>
                      <div style={{ fontWeight: '500', color: '#1f2937' }}>{file.name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{formatSize(file.size)}</div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                    disabled={uploading}
                    style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#fef2f2',
                      color: '#dc3545',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 2rem',
                backgroundColor: uploading ? '#9ca3af' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length > 1 ? 's' : ''}`}
            </button>
          </div>
        )}
      </div>

      {/* Uploaded Files List */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#1f2937' }}>
          📂 Uploaded Files ({uploadedFiles.length})
        </h2>

        {uploadedFiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
            No files uploaded yet
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>File</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Category</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Size</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Uploaded</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>By</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles.map((file, idx) => (
                  <tr key={file.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{getFileIcon(file.filename)}</span>
                        <span style={{ fontWeight: '500' }}>{file.original_name || file.filename}</span>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', 
                        backgroundColor: '#e0f2fe', 
                        color: '#0369a1', 
                        borderRadius: '4px',
                        fontSize: '0.8rem'
                      }}>
                        {file.category || 'general'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                      {formatSize(file.size || 0)}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                      {file.created_at ? new Date(file.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                      {file.uploaded_by || '-'}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <a
                          href={`/uploads/${file.category || 'general'}/${file.filename}`}
                          download
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            textDecoration: 'none'
                          }}
                        >
                          Download
                        </a>
                        <button
                          onClick={() => handleDelete(file.id, file.original_name || file.filename)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#fef2f2',
                            color: '#dc3545',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          backgroundColor: toast.type === 'error' ? '#fef2f2' : '#dcfce7',
          color: toast.type === 'error' ? '#dc3545' : '#166534',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default FileUploadPage;
